import { Box, Flex, Text } from '@common/fundamentals'
import { IconButton, Card, Button } from '@common/material'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CardContent,
  ListItem,
  List,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  ISiteBoxDocumentConfigResult,
  SiteBoxDocumentConfig,
} from '@core/entities/SiteBoxDocumentConfig'
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material'
import { Palette } from '@core/theme/color-palette-siteassist'
import { localizedStrings } from '@core/strings'
import React from 'react'

type QueryResultDetailsProps = {
  result: ISiteBoxDocumentConfigResult
  clickedEvidenceIndex: number | null
  onEvidenceClick: (index: number, quote: string, pageNumber?: number) => void
  searchResultInfo?: { current: number; total: number } | null
  onNextResult?: () => void
  onPreviousResult?: () => void
}

export const QueryResultDetails: React.FC<QueryResultDetailsProps> = ({
  result: selectedResult,
  clickedEvidenceIndex,
  onEvidenceClick,
  searchResultInfo,
  onNextResult,
  onPreviousResult,
}) => {
  const { query, result } = selectedResult
  const hasEvidence = result?.evidence && result.evidence.length > 0

  return (
    <>
      <Box
        border='1px solid'
        borderColor={Palette.Grey[400]}
        borderRadius={1}
        p={2}
        mb={2}
      >
        <Text gutterBottom fontSize={13}>
          {localizedStrings.question}
        </Text>
        <Text fontSize={14}>{query}</Text>
      </Box>
      <Box>
        {result?.output?.stringList?.length > 0 ? (
          <List disablePadding sx={{ listStyleType: 'disc', pl: 2 }}>
            {result.output.stringList.map((item) => (
              <ListItem
                key={item}
                disablePadding
                sx={{ display: 'list-item', py: 0.2 }}
              >
                {item}
              </ListItem>
            ))}
          </List>
        ) : (
          <Text flex={1} variant='body2' sx={{ whiteSpace: 'pre-line' }}>
            {SiteBoxDocumentConfig.getDisplayValue(result?.output)}
          </Text>
        )}
      </Box>

      {result?.reasoning && (
        <Accordion
          defaultExpanded
          elevation={0}
          disableGutters
          sx={{ '&:before': { display: 'none' }, overflow: 'hidden' }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: 0, flexDirection: 'row-reverse', gap: 1 }}
          >
            <Text>{localizedStrings.reasoning}</Text>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <Text variant='body2'>{result.reasoning}</Text>
          </AccordionDetails>
        </Accordion>
      )}

      {hasEvidence && (
        <Accordion
          defaultExpanded
          elevation={0}
          disableGutters
          sx={{ '&:before': { display: 'none' }, overflow: 'hidden' }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: 0, flexDirection: 'row-reverse', gap: 1 }}
          >
            <Text>{localizedStrings.evidenceFromDocument}</Text>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            {result.evidence.map((evidence, index) => (
              <Box key={`evidence-${index}-${evidence.quote.substring(0, 50)}`}>
                <Card
                  variant='outlined'
                  sx={{
                    mb: 1,
                    cursor: 'pointer',
                    border: '1px solid',
                    bgcolor:
                      clickedEvidenceIndex === index
                        ? Palette.Violet[50]
                        : undefined,
                    borderColor:
                      clickedEvidenceIndex === index
                        ? Palette.Violet[400]
                        : Palette.Grey[400],
                    '&:hover': {
                      backgroundColor:
                        clickedEvidenceIndex === index
                          ? Palette.Violet[100]
                          : Palette.Grey[100],
                    },
                  }}
                  onClick={() =>
                    onEvidenceClick(
                      index,
                      evidence.quote,
                      evidence.location?.[0]?.page,
                    )
                  }
                >
                  <CardContent sx={{ py: 1.5 }}>
                    <Text variant='body2' sx={{ mb: 1 }}>
                      {evidence.quote}
                    </Text>

                    <Flex
                      gap={1}
                      justifyContent='space-between'
                      alignItems='center'
                    >
                      <Flex gap={1} flexWrap='wrap'>
                        {evidence.location.map((location, locationIndex) => (
                          <Button
                            key={`${evidence.quote}-page-${location.page}-${locationIndex}`}
                            variant='outlined'
                            size='small'
                          >
                            {localizedStrings.page} {location.page}
                          </Button>
                        ))}
                      </Flex>
                      {clickedEvidenceIndex === index &&
                        searchResultInfo &&
                        searchResultInfo.total > 1 && (
                          <Flex
                            alignItems='center'
                            gap={1}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconButton
                              size='small'
                              onClick={(e) => {
                                e.stopPropagation()
                                onPreviousResult?.()
                              }}
                              disabled={searchResultInfo.total <= 1}
                            >
                              <KeyboardArrowLeft
                                sx={{ height: '20px', width: '20px' }}
                              />
                            </IconButton>

                            <Text
                              variant='caption'
                              sx={{
                                textAlign: 'center',
                                userSelect: 'none',
                              }}
                            >
                              {`${searchResultInfo.current} of ${searchResultInfo.total}`}
                            </Text>

                            <IconButton
                              size='small'
                              onClick={(e) => {
                                e.stopPropagation()
                                onNextResult?.()
                              }}
                              disabled={searchResultInfo.total <= 1}
                            >
                              <KeyboardArrowRight
                                sx={{ height: '20px', width: '20px' }}
                              />
                            </IconButton>
                          </Flex>
                        )}
                    </Flex>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  )
}
